import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { ReportThisEvent as ReportThisEventModal } from '@eventbrite/legacy.report-this-event';
import { FlagIcon } from '../_shared/Icons';
import { FormattedMessage } from 'react-intl';
import styles from './ReportThisEventButton.module.scss';
import { useUserData } from '@/hooks/useUserData';

interface ReportThisEventButtonProps {
  eventId: string;
  eventName: string;
  eventUrl: string;
  country: string;
  locale: string;
  showModal?: boolean;
  onModalClose?: () => void;
}

export interface ReportThisEventButtonRef {
  triggerModal: () => void;
}

export const ReportThisEventButton = forwardRef<ReportThisEventButtonRef, ReportThisEventButtonProps>(
  ({ eventId, eventName, eventUrl, country, locale, showModal = false, onModalClose }, ref) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const { userId, isLoading } = useUserData();

    useImperativeHandle(ref, () => ({
      triggerModal: () => setIsModalVisible(true),
    }));

    useEffect(() => {
      if (showModal) {
        setIsModalVisible(true);
      }
    }, [showModal]);

    const handleButtonClick = () => {
      setIsModalVisible(true);
    };

    const handleModalClose = () => {
      setIsModalVisible(false);
      onModalClose?.();
    };

    return (
      <>
        <div
          id="report-this-event-button"
          className={styles.reportThisEventButton}
          onClick={handleButtonClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleButtonClick();
            }
          }}
          data-testid="report-this-event-button"
        >
          <FlagIcon />
          <div className={styles.reportThisEventButtonText}>
            <FormattedMessage
              id="Report this event"
              defaultMessage="Report this event"
              description="Report this event text"
            />
          </div>
        </div>

        <ReportThisEventModal
          isVisible={isModalVisible}
          domNodeId="__next"
          eventId={eventId}
          isAuthenticated={!isLoading && !!userId}
          eventName={eventName}
          eventUrl={eventUrl}
          country={country}
          helpCenterLanguage={locale}
          id="report-event-modal"
          onClose={handleModalClose}
        />
      </>
    );
  },
);

ReportThisEventButton.displayName = 'ReportThisEventButton';
